import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const C = {
    card: 'rgba(13,19,50,0.68)',
    border: 'rgba(255,255,255,0.09)',
    cyan: '#00d4ff',
    green: '#22c55e',
    red: '#ff4d5d',
    amber: '#fbbf24',
    purple: '#a78bfa',
    muted: 'rgba(255,255,255,0.45)',
};

const fieldStyle = {
    width: '100%', minHeight: 42, padding: '10px 12px', borderRadius: 10,
    border: `1px solid ${C.border}`, background: 'rgba(0,0,0,0.24)',
    color: '#fff', outline: 'none', fontSize: 13,
};

const cardStyle = {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 18,
    overflow: 'hidden', backdropFilter: 'blur(14px)',
};

const labelStyle = {
    display: 'block', marginBottom: 7, fontSize: 10, color: C.muted,
    fontWeight: 800, letterSpacing: 0.8, textTransform: 'uppercase',
};

const toList = value => String(value || '').split(/[\n,]/).map(v => v.trim()).filter(Boolean);
const formatDate = value => {
    if (!value) return '—';
    try { return new Date(Number(value) > 10000000000 ? Number(value) : Number(value) * 1000 || value).toLocaleString(); }
    catch (_) { return '—'; }
};
const getMessageTitle = item => item?.headings?.en || item?.name || item?.contents?.en || 'Untitled notification';
const getMessageBody = item => item?.contents?.en || '';
const number = value => Number(value || 0).toLocaleString();

const Button = ({ children, onClick, disabled, tone = 'default', type = 'button', style }) => {
    const tones = {
        default: { color: '#dce8ff', border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.05)' },
        primary: { color: '#041225', border: 'none', background: `linear-gradient(135deg,${C.cyan},#2583ff)`, fontWeight: 900 },
        danger: { color: '#ff8d98', border: '1px solid rgba(255,77,93,.35)', background: 'rgba(255,77,93,.1)' },
        success: { color: '#86efac', border: '1px solid rgba(34,197,94,.35)', background: 'rgba(34,197,94,.1)' },
    };
    return (
        <button type={type} onClick={onClick} disabled={disabled} style={{
            minHeight: 40, padding: '8px 14px', borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1, fontSize: 12, ...tones[tone], ...style,
        }}>{children}</button>
    );
};

const SectionHead = ({ title, subtitle, right }) => (
    <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 14 }}>{title}</div>
            {subtitle && <div style={{ color: C.muted, fontSize: 11, marginTop: 4 }}>{subtitle}</div>}
        </div>
        {right}
    </div>
);

const defaultForm = () => ({
    name: '', title: 'Ludo Room', subtitle: '', message: '', targetType: 'all',
    segments: 'Subscribed Users', externalIds: '', subscriptionIds: '', filtersJson: '[]',
    url: '', image: '', dataJson: '{}', sendAfter: '', priority: '10', ttl: '259200',
    androidChannelId: '', collapseId: '',
});

export default function OneSignalAdmin({ backendUrl }) {
    const base = `${backendUrl}/api/admin/onesignal`;
    const [status, setStatus] = useState(null);
    const [messages, setMessages] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [form, setForm] = useState(defaultForm);
    const [selected, setSelected] = useState(null);
    const [offset, setOffset] = useState(0);
    const limit = 25;

    const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    const loadStatus = useCallback(async () => {
        try {
            const res = await axios.get(`${base}/status`);
            setStatus(res.data);
        } catch (err) {
            setStatus(err.response?.data || { configured: false, connected: false });
        }
    }, [base]);

    const loadMessages = useCallback(async (nextOffset = offset) => {
        try {
            setLoading(true); setError('');
            const res = await axios.get(`${base}/messages`, { params: { limit, offset: nextOffset } });
            const list = res.data.notifications || res.data.messages || [];
            setMessages(Array.isArray(list) ? list : []);
            setTotalCount(Number(res.data.total_count ?? res.data.total ?? list.length));
            setOffset(nextOffset);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not load OneSignal messages');
        } finally { setLoading(false); }
    }, [base, offset]);

    useEffect(() => { loadStatus(); loadMessages(0); }, [loadStatus, loadMessages]);

    const analytics = useMemo(() => messages.reduce((acc, item) => {
        acc.sent += Number(item.successful || item.recipients || 0);
        acc.received += Number(item.received || 0);
        acc.clicked += Number(item.converted || item.clicked || 0);
        acc.failed += Number(item.failed || 0);
        return acc;
    }, { sent: 0, received: 0, clicked: 0, failed: 0 }), [messages]);

    const buildPayload = () => {
        let data = {};
        let filters = [];
        try { data = form.dataJson.trim() ? JSON.parse(form.dataJson) : {}; }
        catch (_) { throw new Error('Custom data must be valid JSON'); }
        if (!data || Array.isArray(data) || typeof data !== 'object') throw new Error('Custom data must be a JSON object');

        if (form.targetType === 'filters') {
            try { filters = JSON.parse(form.filtersJson); }
            catch (_) { throw new Error('Filters must be valid JSON'); }
            if (!Array.isArray(filters) || !filters.length) throw new Error('Filters must be a non-empty JSON array');
        }

        const sendAfter = form.sendAfter ? new Date(form.sendAfter) : null;
        if (sendAfter && Number.isNaN(sendAfter.getTime())) throw new Error('Schedule date is invalid');

        return {
            name: form.name.trim(), title: form.title.trim(), subtitle: form.subtitle.trim(),
            message: form.message.trim(), targetType: form.targetType,
            segments: toList(form.segments), externalIds: toList(form.externalIds),
            subscriptionIds: toList(form.subscriptionIds), filters,
            url: form.url.trim(), image: form.image.trim(), data,
            sendAfter: sendAfter ? sendAfter.toISOString() : '',
            priority: form.priority === '' ? undefined : Number(form.priority),
            ttl: form.ttl === '' ? undefined : Number(form.ttl),
            androidChannelId: form.androidChannelId.trim(), collapseId: form.collapseId.trim(),
        };
    };

    const sendNotification = async event => {
        event.preventDefault();
        try {
            setSending(true); setError(''); setSuccess('');
            const payload = buildPayload();
            if (!payload.message) throw new Error('Notification message is required');
            const res = await axios.post(`${base}/messages`, payload);
            setSuccess(`Notification accepted${res.data?.id ? ` • ${res.data.id}` : ''}`);
            setForm(defaultForm());
            await Promise.all([loadStatus(), loadMessages(0)]);
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Notification failed');
        } finally { setSending(false); }
    };

    const showDetails = async id => {
        try {
            setError('');
            const res = await axios.get(`${base}/messages/${id}`);
            setSelected(res.data);
        } catch (err) { setError(err.response?.data?.message || 'Could not load notification details'); }
    };

    const cancelMessage = async item => {
        const id = item?.id;
        if (!id || !window.confirm(`Cancel notification ${id}?`)) return;
        try {
            setError(''); setSuccess('');
            await axios.delete(`${base}/messages/${id}`);
            setSuccess('Notification cancelled');
            loadMessages(offset);
        } catch (err) { setError(err.response?.data?.message || 'Could not cancel notification'); }
    };

    const stat = (label, value, color, icon) => (
        <div style={{ ...cardStyle, padding: 18, borderTop: `3px solid ${color}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div>
                    <div style={{ fontSize: 28, fontWeight: 900, color }}>{number(value)}</div>
                    <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 1, marginTop: 5 }}>{label}</div>
                </div>
                <span style={{ fontSize: 22, opacity: 0.45 }}>{icon}</span>
            </div>
        </div>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {(error || success) && <div style={{ padding: '12px 16px', borderRadius: 12, border: `1px solid ${error ? 'rgba(255,77,93,.35)' : 'rgba(34,197,94,.35)'}`, background: error ? 'rgba(255,77,93,.1)' : 'rgba(34,197,94,.1)', color: error ? '#ff8d98' : '#86efac', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span>{error || success}</span><button onClick={() => { setError(''); setSuccess(''); }} style={{ border: 0, background: 'none', color: 'inherit', cursor: 'pointer', fontSize: 18 }}>×</button>
            </div>}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12 }}>
                {stat('Connection', status?.connected ? 'Online' : 'Offline', status?.connected ? C.green : C.red, '🔌')}
                {stat('Messages', totalCount || status?.messageCount, C.cyan, '🔔')}
                {stat('Recent sent', analytics.sent, C.purple, '🚀')}
                {stat('Recent clicks', analytics.clicked, C.amber, '👆')}
            </div>

            <div style={{ ...cardStyle, padding: '13px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                    <div style={{ color: '#fff', fontWeight: 800 }}>OneSignal Android SDK {status?.sdkVersion || '5.9.1'}</div>
                    <div style={{ color: C.muted, fontSize: 11, marginTop: 3 }}>App ID: {status?.appId || 'Not configured'} • API key: {status?.apiKeyConfigured ? 'server-side configured' : 'missing'}</div>
                </div>
                <Button onClick={() => Promise.all([loadStatus(), loadMessages(offset)])}>↻ Refresh</Button>
            </div>

            <form onSubmit={sendNotification} style={cardStyle}>
                <SectionHead title="🔔 Compose push notification" subtitle="Broadcast, segment, external-ID, subscription-ID, filter, deep-link, image, data, and scheduled delivery controls" />
                <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 12 }}>
                        <div><label style={labelStyle}>Internal name</label><input style={fieldStyle} value={form.name} onChange={e => update('name', e.target.value)} placeholder="Weekend tournament reminder" /></div>
                        <div><label style={labelStyle}>Title</label><input style={fieldStyle} value={form.title} onChange={e => update('title', e.target.value)} placeholder="Ludo Room" /></div>
                        <div><label style={labelStyle}>Subtitle</label><input style={fieldStyle} value={form.subtitle} onChange={e => update('subtitle', e.target.value)} placeholder="Optional Android subtitle" /></div>
                    </div>
                    <div><label style={labelStyle}>Message *</label><textarea style={{ ...fieldStyle, minHeight: 100, resize: 'vertical' }} value={form.message} onChange={e => update('message', e.target.value)} placeholder="Write the push notification body…" /></div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px,.7fr) minmax(260px,2fr)', gap: 12 }}>
                        <div><label style={labelStyle}>Audience</label><select style={fieldStyle} value={form.targetType} onChange={e => update('targetType', e.target.value)}>
                            <option value="all">All subscribed users</option><option value="segments">Segments</option><option value="external_ids">External IDs</option><option value="subscription_ids">Subscription IDs</option><option value="filters">Advanced filters</option>
                        </select></div>
                        <div>
                            {form.targetType === 'all' && <><label style={labelStyle}>Target</label><input style={fieldStyle} value="Subscribed Users" disabled /></>}
                            {form.targetType === 'segments' && <><label style={labelStyle}>Segments</label><textarea style={{ ...fieldStyle, minHeight: 70 }} value={form.segments} onChange={e => update('segments', e.target.value)} placeholder="Subscribed Users, Active Users" /></>}
                            {form.targetType === 'external_ids' && <><label style={labelStyle}>External IDs</label><textarea style={{ ...fieldStyle, minHeight: 70 }} value={form.externalIds} onChange={e => update('externalIds', e.target.value)} placeholder="user-123, user-456" /></>}
                            {form.targetType === 'subscription_ids' && <><label style={labelStyle}>Subscription IDs</label><textarea style={{ ...fieldStyle, minHeight: 70 }} value={form.subscriptionIds} onChange={e => update('subscriptionIds', e.target.value)} placeholder="OneSignal subscription UUIDs" /></>}
                            {form.targetType === 'filters' && <><label style={labelStyle}>Filters JSON</label><textarea style={{ ...fieldStyle, minHeight: 90, fontFamily: 'monospace' }} value={form.filtersJson} onChange={e => update('filtersJson', e.target.value)} placeholder='[{"field":"tag","key":"level","relation":">","value":"10"}]' /></>}
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 12 }}>
                        <div><label style={labelStyle}>Deep-link / URL</label><input style={fieldStyle} type="url" value={form.url} onChange={e => update('url', e.target.value)} placeholder="https://example.com/play" /></div>
                        <div><label style={labelStyle}>Big picture URL</label><input style={fieldStyle} type="url" value={form.image} onChange={e => update('image', e.target.value)} placeholder="https://example.com/banner.jpg" /></div>
                        <div><label style={labelStyle}>Schedule</label><input style={fieldStyle} type="datetime-local" value={form.sendAfter} onChange={e => update('sendAfter', e.target.value)} /></div>
                    </div>

                    <div><label style={labelStyle}>Custom data JSON</label><textarea style={{ ...fieldStyle, minHeight: 82, fontFamily: 'monospace' }} value={form.dataJson} onChange={e => update('dataJson', e.target.value)} placeholder='{"screen":"room","roomCode":"12345678"}' /></div>

                    <details style={{ border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, background: 'rgba(0,0,0,.12)' }}>
                        <summary style={{ cursor: 'pointer', color: '#dce8ff', fontWeight: 800, fontSize: 12 }}>Advanced Android delivery</summary>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginTop: 14 }}>
                            <div><label style={labelStyle}>Priority</label><select style={fieldStyle} value={form.priority} onChange={e => update('priority', e.target.value)}><option value="10">High</option><option value="5">Normal</option></select></div>
                            <div><label style={labelStyle}>TTL seconds</label><input style={fieldStyle} type="number" min="0" value={form.ttl} onChange={e => update('ttl', e.target.value)} /></div>
                            <div><label style={labelStyle}>Android channel ID</label><input style={fieldStyle} value={form.androidChannelId} onChange={e => update('androidChannelId', e.target.value)} /></div>
                            <div><label style={labelStyle}>Collapse ID</label><input style={fieldStyle} value={form.collapseId} onChange={e => update('collapseId', e.target.value)} /></div>
                        </div>
                    </details>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, flexWrap: 'wrap' }}>
                        <Button onClick={() => setForm(defaultForm())}>Reset</Button>
                        <Button type="submit" tone="primary" disabled={sending || !status?.connected}>{sending ? 'Sending…' : form.sendAfter ? 'Schedule notification' : 'Send notification'}</Button>
                    </div>
                </div>
            </form>

            <div style={cardStyle}>
                <SectionHead title="📨 Recent OneSignal messages" subtitle={`${totalCount} total messages`} right={<Button onClick={() => loadMessages(offset)} disabled={loading}>{loading ? 'Loading…' : '↻ Refresh'}</Button>} />
                {loading ? <div style={{ padding: 50, textAlign: 'center', color: C.muted }}>Loading OneSignal messages…</div> : messages.length === 0 ? <div style={{ padding: 50, textAlign: 'center', color: C.muted }}>No notifications found.</div> : (
                    <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', minWidth: 900, borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead><tr>{['Message', 'Sent', 'Received', 'Clicked', 'Failed', 'Status', 'Actions'].map(h => <th key={h} style={{ textAlign: h === 'Actions' ? 'right' : 'left', padding: '12px 14px', color: C.muted, fontSize: 10, textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
                        <tbody>{messages.map(item => {
                            const scheduled = item.send_after && !item.completed_at;
                            const statusText = item.canceled ? 'cancelled' : scheduled ? 'scheduled' : item.completed_at ? 'sent' : 'processing';
                            const statusColor = item.canceled ? C.red : scheduled ? C.amber : item.completed_at ? C.green : C.cyan;
                            return <tr key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,.045)' }}>
                                <td style={{ padding: '12px 14px', maxWidth: 350 }}><div style={{ color: '#fff', fontWeight: 800 }}>{getMessageTitle(item)}</div><div style={{ color: C.muted, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getMessageBody(item)}</div><div style={{ color: 'rgba(255,255,255,.25)', fontFamily: 'monospace', fontSize: 9, marginTop: 4 }}>{item.id}</div></td>
                                <td style={{ padding: 14 }}>{number(item.successful || item.recipients)}</td><td style={{ padding: 14 }}>{number(item.received)}</td><td style={{ padding: 14 }}>{number(item.converted || item.clicked)}</td><td style={{ padding: 14, color: Number(item.failed) ? C.red : 'inherit' }}>{number(item.failed)}</td>
                                <td style={{ padding: 14 }}><span style={{ padding: '4px 9px', borderRadius: 20, color: statusColor, border: `1px solid ${statusColor}55`, background: `${statusColor}15`, fontWeight: 800, fontSize: 10 }}>{statusText}</span><div style={{ color: C.muted, marginTop: 5, fontSize: 10 }}>{formatDate(item.send_after || item.completed_at || item.queued_at)}</div></td>
                                <td style={{ padding: 14 }}><div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}><Button onClick={() => showDetails(item.id)}>Details</Button>{!item.canceled && !item.completed_at && <Button tone="danger" onClick={() => cancelMessage(item)}>Cancel</Button>}</div></td>
                            </tr>;
                        })}</tbody>
                    </table></div>
                )}
                <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${C.border}` }}>
                    <Button disabled={offset <= 0} onClick={() => loadMessages(Math.max(0, offset - limit))}>← Previous</Button>
                    <span style={{ color: C.muted, fontSize: 11 }}>{offset + 1}–{Math.min(offset + limit, totalCount || offset + messages.length)} of {totalCount}</span>
                    <Button disabled={offset + limit >= totalCount} onClick={() => loadMessages(offset + limit)}>Next →</Button>
                </div>
            </div>

            {selected && <div onClick={() => setSelected(null)} style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(0,0,0,.78)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                <div onClick={e => e.stopPropagation()} style={{ width: 'min(760px,100%)', maxHeight: '88vh', overflow: 'auto', borderRadius: 20, background: '#0a102c', border: `1px solid ${C.border}`, boxShadow: '0 25px 80px rgba(0,0,0,.8)' }}>
                    <SectionHead title="Notification details" subtitle={selected.id} right={<Button onClick={() => setSelected(null)}>× Close</Button>} />
                    <pre style={{ margin: 0, padding: 20, color: '#cdefff', fontSize: 11, lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{JSON.stringify(selected, null, 2)}</pre>
                </div>
            </div>}
        </div>
    );
}
